import { useState, useRef, useCallback, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/apiClient';
import { Move, ZoomIn, ZoomOut, RotateCcw, Lock, Unlock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface Ply {
  id: string;
  ply_number: number;
  material_name?: string;
  material_type?: string;
  color?: string;
  position_x: number;
  position_y: number;
  visual_width: number;
  visual_height: number;
  zone?: string;
}

interface PlybookLayerEditorProps {
  imageUrl: string;
  plies: Ply[];
  onPlySelect?: (ply: Ply) => void;
  selectedPlyId?: string;
  editable?: boolean;
}

const DEFAULT_COLORS: Record<string, string> = {
  carbon: '#1e293b',
  glass: '#0ea5e9',
  aramid: '#eab308',
  core: '#f97316',
  adhesive: '#a855f7',
  other: '#6b7280',
};

export function PlybookLayerEditor({
  imageUrl,
  plies,
  onPlySelect,
  selectedPlyId,
  editable = true,
}: PlybookLayerEditorProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState<string | null>(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [localPositions, setLocalPositions] = useState<Record<string, { x: number; y: number }>>({});
  const [scale, setScale] = useState(1);
  const [locked, setLocked] = useState(false);
  const queryClient = useQueryClient();

  useEffect(() => {
    const positions: Record<string, { x: number; y: number }> = {};
    plies.forEach(ply => {
      positions[ply.id] = { x: ply.position_x || 0, y: ply.position_y || 0 };
    });
    setLocalPositions(positions);
  }, [plies]);

  const updatePositionMutation = useMutation({
    mutationFn: async ({ plyId, x, y }: { plyId: string; x: number; y: number }) => {
      await api.patch('plybook_plies', { position_x: x, position_y: y }, { id: 'eq.' + plyId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plybook-plies'] });
    },
  });

  const getPosition = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    if (!containerRef.current) return { x: 0, y: 0 };
    const rect = containerRef.current.getBoundingClientRect();
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
    return {
      x: ((clientX - rect.left) / rect.width) * 100,
      y: ((clientY - rect.top) / rect.height) * 100,
    };
  }, []);

  const handleStart = useCallback((e: React.MouseEvent | React.TouchEvent, ply: Ply) => {
    if (!editable || locked) return;
    e.preventDefault();
    e.stopPropagation();

    const pos = getPosition(e);
    const currentPos = localPositions[ply.id] || { x: ply.position_x || 0, y: ply.position_y || 0 };

    setDragOffset({
      x: pos.x - currentPos.x,
      y: pos.y - currentPos.y,
    });
    setDragging(ply.id);
    onPlySelect?.(ply);
  }, [editable, locked, getPosition, localPositions, onPlySelect]);

  const handleMove = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    if (!dragging) return;
    e.preventDefault();

    const pos = getPosition(e);
    const newX = Math.max(0, Math.min(100, pos.x - dragOffset.x));
    const newY = Math.max(0, Math.min(100, pos.y - dragOffset.y));

    setLocalPositions(prev => ({
      ...prev,
      [dragging]: { x: newX, y: newY },
    }));
  }, [dragging, dragOffset, getPosition]);

  const handleEnd = useCallback(() => {
    if (dragging) {
      const pos = localPositions[dragging];
      if (pos) {
        updatePositionMutation.mutate({ plyId: dragging, x: pos.x, y: pos.y });
      }
    }
    setDragging(null);
  }, [dragging, localPositions, updatePositionMutation]);

  const getPlyColor = (ply: Ply) => {
    return ply.color || DEFAULT_COLORS[ply.material_type || 'other'] || DEFAULT_COLORS.other;
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => setScale(s => Math.max(0.5, s - 0.25))}
            className="h-8 w-8 p-0"
          >
            <ZoomOut className="w-4 h-4" />
          </Button>
          <span className="text-sm text-white/70 min-w-[3rem] text-center">{Math.round(scale * 100)}%</span>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setScale(s => Math.min(2, s + 0.25))}
            className="h-8 w-8 p-0"
          >
            <ZoomIn className="w-4 h-4" />
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setScale(1)}
            className="h-8 w-8 p-0"
          >
            <RotateCcw className="w-4 h-4" />
          </Button>
        </div>
        <Button
          size="sm"
          variant={locked ? 'destructive' : 'outline'}
          onClick={() => setLocked(!locked)}
          className="gap-1"
        >
          {locked ? <Lock className="w-4 h-4" /> : <Unlock className="w-4 h-4" />}
          {locked ? 'Bloqueado' : 'Editable'}
        </Button>
      </div>

      <div
        className="overflow-auto rounded-xl bg-black/30 p-2"
        style={{ maxHeight: '60vh' }}
      >
        <div
          ref={containerRef}
          className="relative mx-auto touch-none select-none"
          style={{
            transform: `scale(${scale})`,
            transformOrigin: 'top center',
            width: 'fit-content',
          }}
          onMouseMove={handleMove}
          onMouseUp={handleEnd}
          onMouseLeave={handleEnd}
          onTouchMove={handleMove}
          onTouchEnd={handleEnd}
        >
          <img
            src={imageUrl}
            alt="Molde"
            className="max-w-full h-auto rounded-lg pointer-events-none"
            style={{ maxHeight: '50vh' }}
            draggable={false}
          />

          {plies.map((ply) => {
            const pos = localPositions[ply.id] || { x: ply.position_x || 0, y: ply.position_y || 0 };
            const isSelected = selectedPlyId === ply.id;
            const isDragging = dragging === ply.id;

            return (
              <div
                key={ply.id}
                className={cn(
                  'absolute flex items-center justify-center rounded cursor-move transition-shadow',
                  'border-2 text-white font-bold text-sm shadow-lg',
                  isSelected && 'ring-2 ring-white ring-offset-2 ring-offset-transparent',
                  isDragging && 'opacity-80 scale-105',
                  !editable || locked ? 'cursor-default' : 'cursor-move hover:scale-102'
                )}
                style={{
                  left: `${pos.x}%`,
                  top: `${pos.y}%`,
                  width: `${ply.visual_width || 20}%`,
                  height: `${ply.visual_height || 15}%`,
                  backgroundColor: `${getPlyColor(ply)}cc`,
                  borderColor: getPlyColor(ply),
                  transform: 'translate(-50%, -50%)',
                  zIndex: isDragging ? 100 : isSelected ? 50 : ply.ply_number,
                }}
                onMouseDown={(e) => handleStart(e, ply)}
                onTouchStart={(e) => handleStart(e, ply)}
                onClick={() => onPlySelect?.(ply)}
              >
                <div className="flex flex-col items-center gap-0.5 p-1 text-center">
                  <span className="text-lg font-bold drop-shadow">{ply.ply_number}</span>
                  <span className="text-[10px] opacity-80 truncate max-w-full px-1">
                    {ply.zone || ply.material_name || 'Capa'}
                  </span>
                </div>
                {editable && !locked && (
                  <Move className="absolute top-1 right-1 w-3 h-3 opacity-50" />
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex flex-wrap gap-2 text-xs">
        {plies.map(ply => (
          <div
            key={ply.id}
            className={cn(
              'flex items-center gap-1 px-2 py-1 rounded-full cursor-pointer transition-all',
              selectedPlyId === ply.id ? 'ring-2 ring-white' : 'opacity-70 hover:opacity-100'
            )}
            style={{ backgroundColor: `${getPlyColor(ply)}99` }}
            onClick={() => onPlySelect?.(ply)}
          >
            <span className="font-bold">{ply.ply_number}</span>
            <span className="hidden sm:inline">{ply.zone || ply.material_type}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
